import { useState } from 'react';
import { FixtureType, FIXTURE_TYPES } from '@/types/fixture';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { 
  Square, 
  Minus, 
  Wine, 
  Store, 
  ArrowUpDown, 
  DoorOpen, 
  TreeDeciduous, 
  Circle,
} from 'lucide-react';

interface AddFixtureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (type: FixtureType, name: string | null, color: string) => void;
}

const FIXTURE_ICONS: Record<FixtureType, React.ComponentType<{ className?: string }>> = {
  rectangle: Square,
  circle: Circle,
  wall: Minus,
  bar: Wine,
  counter: Store,
  stairs: ArrowUpDown,
  door: DoorOpen,
  plant: TreeDeciduous,
};

export function AddFixtureDialog({ open, onOpenChange, onAdd }: AddFixtureDialogProps) {
  const [selectedType, setSelectedType] = useState<FixtureType>('rectangle');
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6b7280');

  const selectedConfig = FIXTURE_TYPES.find(t => t.type === selectedType);

  const handleAdd = () => {
    onAdd(selectedType, name.trim() || null, color);
    setName('');
    setSelectedType('rectangle');
    setColor('#6b7280');
    onOpenChange(false);
  };

  const handleSelectType = (type: FixtureType) => {
    setSelectedType(type);
    const config = FIXTURE_TYPES.find(t => t.type === type);
    if (config) {
      setColor(config.defaultColor);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Ajouter un élément fixe</DialogTitle>
          <DialogDescription>
            Ajoutez un élément visuel pour aider à l'orientation sur le plan de salle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Type selection grid */}
          <div className="space-y-2">
            <Label>Type d'élément</Label>
            <div className="grid grid-cols-4 gap-2">
              {FIXTURE_TYPES.map(config => {
                const Icon = FIXTURE_ICONS[config.type];
                return (
                  <button
                    key={config.type}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 p-3 rounded-lg border-2 transition-all",
                      selectedType === config.type
                        ? "border-primary bg-primary/10"
                        : "border-muted hover:border-muted-foreground/50 hover:bg-muted/50"
                    )}
                    onClick={() => handleSelectType(config.type)}
                  >
                    <div
                      className="w-8 h-8 rounded flex items-center justify-center"
                      style={{ backgroundColor: config.defaultColor }}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs font-medium">{config.label}</span>
                  </button>
                );
              })}
            </div>
            {selectedConfig && (
              <p className="text-xs text-muted-foreground mt-2">
                {selectedConfig.description}
              </p>
            )}
          </div>

          {/* Name input */}
          <div className="space-y-2">
            <Label htmlFor="fixture-name">Nom (optionnel)</Label>
            <Input
              id="fixture-name"
              placeholder="Ex: Bar principal, Entrée..."
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Color picker */}
          <div className="space-y-2">
            <Label>Couleur</Label>
            <div className="flex items-center gap-3">
              <Input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 p-0 border-0 cursor-pointer"
              />
              <Input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-28"
                placeholder="#000000"
              />
              <div className="flex gap-1">
                {['#374151', '#6b7280', '#92400e', '#78716c', '#64748b', '#a16207', '#15803d', '#1e40af'].map(c => (
                  <button
                    key={c}
                    className={cn(
                      "w-7 h-7 rounded border-2 transition-colors",
                      color === c ? "border-foreground" : "border-transparent hover:border-muted-foreground/50"
                    )}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Aperçu</Label>
            <div className="flex items-center justify-center p-6 bg-muted/30 rounded-lg border">
              <div
                className={cn(
                  "w-20 h-12 flex items-center justify-center",
                  selectedType === 'circle' && "rounded-full",
                  selectedType === 'wall' && "rounded-sm",
                  selectedType !== 'circle' && selectedType !== 'wall' && "rounded"
                )}
                style={{ backgroundColor: color }}
              >
                {(() => {
                  const Icon = FIXTURE_ICONS[selectedType];
                  return <Icon className="w-6 h-6 text-white/80" />;
                })()}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleAdd}>
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
